import type { PlayerState, Quat, Vec3 } from '../../../../vrroom_typescript/src/platform/contracts';

/** Server timestamps only; late keyframes must never rewind a rendered player. */
export class SnapshotBuffer {
    private frames: PlayerState[] = [];
    push(state: PlayerState): void {
        const last = this.frames[this.frames.length - 1];
        if (last) {
            if (state.membershipEpoch < last.membershipEpoch ||
                (state.membershipEpoch === last.membershipEpoch && state.poseRevision < last.poseRevision)) return;
            if (state.membershipEpoch !== last.membershipEpoch || state.poseRevision !== last.poseRevision) this.frames = [];
            else if (state.seq < last.seq || (state.seq === last.seq && state.sampleTime <= last.sampleTime)) return;
        }
        this.frames.push(structuredClone(state));
        if (this.frames.length > 32) this.frames.shift();
    }
    sample(time: number): PlayerState | null {
        if (!this.frames.length) return null;
        const first = this.frames[0], last = this.frames[this.frames.length - 1]!;
        if (time <= first.sampleTime) return structuredClone(first);
        for (let i = 1; i < this.frames.length; i++) {
            const a = this.frames[i - 1], b = this.frames[i];
            if (time > b.sampleTime) continue;
            const t = (time - a.sampleTime) / Math.max(1, b.sampleTime - a.sampleTime);
            return { ...structuredClone(a), position: a.position.map((v, j) => v + (b.position[j] - v) * t) as Vec3,
                rotation: slerp(a.rotation, b.rotation, t) };
        }
        const dt = Math.min(200, Math.max(0, time - last.sampleTime)) / 1000;
        return { ...structuredClone(last), position: last.position.map((v, j) => v + last.velocity[j] * dt) as Vec3 };
    }
}

function slerp(a: Quat, b: Quat, t: number): Quat {
    let dot = a.reduce((sum, v, i) => sum + v * b[i], 0);
    if (dot < 0) { b = b.map(v => -v) as Quat; dot = -dot; }
    let x = 1 - t, y = t;
    if (dot < 0.9995) { const angle = Math.acos(Math.min(1, dot)), sin = Math.sin(angle); x = Math.sin((1 - t) * angle) / sin; y = Math.sin(t * angle) / sin; }
    const q = a.map((v, i) => v * x + b[i] * y) as Quat;
    const norm = Math.hypot(...q);
    return q.map(v => v / norm) as Quat;
}

